import { Directive, EventEmitter, inject, Input, OnChanges, Output } from '@angular/core';
import { PositionsFacade } from '@zero/shared/util-global-state';
import { Asset, ExecutionType, InstrumentType, ProtectOrderModel, Quote } from '@zero/shared/util-models';
import { firstValueFrom } from 'rxjs';

@Directive()
export class ProtectOrderBase implements OnChanges {
  private readonly positionsFacade = inject(PositionsFacade);

  @Input() public asset?: Asset;
  @Input() public quantity?: number | string;
  @Input() public execType?: ExecutionType;
  @Input() public direction?: string;
  @Input() public quote?: Quote;

  public form?: ProtectOrderModel;
  public stopText = '0';

  public distances: number[] = [0, 5, 8, 10, 12, 15, 20];
  public isValid = false;
  public isOrderExecuted = true;

  @Output() public protectOrder = new EventEmitter<ProtectOrderModel>();
  @Output() public protectOrderActionsVisible = new EventEmitter<void>();
  @Output() public furtherOrder = new EventEmitter<void>();

  public get quantityAsNumber(): number {
    return this.quantity !== undefined ? Number(this.quantity) : 0;
  }

  private setFormComplete = false;

  public ngOnChanges(): void {
    if (!this.shouldDisplayForm()) {
      return;
    }

    !this.setFormComplete && this.setForm();
    this.protectOrderActionsVisible.emit();
  }

  public async onSubmit(): Promise<void> {
    this.positionsFacade.loadPositions();
    const position = await firstValueFrom(this.positionsFacade.getPositionByIsin$(this.asset?.isin));
    this.isOrderExecuted = position?.availableQuantity >= Number(this.quantity);
    if (!this.isOrderExecuted) {
      return;
    }

    this.protectOrder.emit(this.form);
  }

  public onChangeStop = (value: number): void => {
    this.form.distance = 0;
    this.form.stop = value;
    this.isValid = this.isValidForm();
  };

  public onChangeValidity = (date: Date): void => {
    this.form.validity = date;
    this.isValid = this.isValidForm();
  };

  public onChangeDistance = (value: number): void => {
    this.setDistance(value);
    this.setStop();
    this.isValid = this.isValidForm();
    this.isOrderExecuted = true;
  };

  private setDistance = (distance: number): void => {
    this.form.distance = distance;
  };

  private setStop = (): void => {
    const { distance } = this.form;
    this.form.stop = distance ? Number((this.quote?.bid * ((100 - distance) / 100)).toFixed(2)) : undefined;
    this.stopText = this.form.stop ? this.form.stop.toString() : '0';
  };

  private setForm = async (): Promise<void> => {
    this.form = new ProtectOrderModel(5);
    this.setStop();
    this.setValidity();
    this.isValid = this.isValidForm();
    this.setFormComplete = true;
  };

  private isValidForm = (): boolean => {
    if (!this.form) {
      return false;
    }
    const { validity, stop } = this.form;
    return Boolean(validity && stop);
  };

  protected setValidity(): void {
    // do nothing
  }

  private shouldDisplayForm = (): boolean => {
    return (
      Number(this.quantity) >= 1 &&
      this.quote?.bid &&
      this.execType === 'market' &&
      this.direction === 'buy' &&
      [InstrumentType.Derivative, InstrumentType.Etf, InstrumentType.Funds, InstrumentType.Share].includes(this.asset?.instrumentType)
    );
  };
}
